import { selectors } from '@hixme/auth-ui'
import { notification } from '@surecompanies/core_components'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import epsClientV1 from 'enrollment-platform/api/eps-v1.client'
import client from 'enrollment-platform/api/eps.client'
import dayjs from 'helpers/datetime'
import { DEFAULT_BENEFIT_EFFECTIVE_DATE } from 'modules/application-engine/constants'
import { useEffect, useMemo, useRef, useState } from 'react'
import { shallowEqual, useSelector } from 'react-redux'
import { GetContributionsResponse } from '../types/get-contribution-response.dto'
import { BenchmarkSnapshot, useContributionsProps } from '../types/types'
import { DEFAULT_BENCHMARK_LABEL } from '../utils'

const STORAGE_KEY = 'ep_contrib_benchmark_snapshot'

export default function useContributions({ id }: useContributionsProps) {
  const userName = useSelector((state) => {
    const { firstname, lastname } = selectors.getCredentials(state)
    return `${firstname} ${lastname}`
  }, shallowEqual)
  const queryClient = useQueryClient()
  const hasInitializedFromCache = useRef(false)
  const [selectedContributionRule, setSelectedContributionRule] =
    useState<any>(null)
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear() + 1 // quick and dirty to get to 2026
  )
  const [selectedBenchmarkSnapshot, setSelectedBenchmarkSnapshot] = useState<
    number | null
  >(null)
  async function getContributions(id: string, year: number) {
    const { data } = await client.get<GetContributionsResponse>(
      `/customers/${id}/contributions/${year}`
    )
    if (data.length > 0) {
      setSelectedContributionRule(data[0])
    }
    return data
  }

  async function getBenchmarkPlans(): Promise<BenchmarkSnapshot[]> {
    const { data } = await epsClientV1.get<{ data: BenchmarkSnapshot[] }>(
      '/benchmark-plans'
    )
    return data?.data ?? []
  }

  const {
    data,
    isPending: isLoading,
    error,
    refetch: refetchContributions,
  } = useQuery({
    queryKey: ['getContributions', id, selectedYear],
    queryFn: () => getContributions(id, selectedYear),
    gcTime: 0,
  })

  const { data: benchmarkSnapshots, isPending: isBenchmarkSnapshotsLoading } =
    useQuery({
      queryKey: ['getBenchmarkPlans'],
      queryFn: getBenchmarkPlans,
      gcTime: 0,
    })

  const yearOptions = useMemo(() => {
    const currentYear = new Date().getFullYear()
    const nextYear = currentYear + 1
    const startYear = dayjs(DEFAULT_BENEFIT_EFFECTIVE_DATE, 'MM-DD-YYYY').year() // Set a reasonable start year for enrollments
    const years: Array<{ value: number; label: string }> = []
    for (let year = startYear; year <= nextYear; year++) {
      years.push({
        value: year,
        label: year.toString(),
      })
    }
    return years.reverse() // Most recent years first
  }, [])

  const benchmarkSnapshotOptions = useMemo(() => {
    const options = [{ value: null, label: DEFAULT_BENCHMARK_LABEL }]
    if (benchmarkSnapshots) {
      const enabledSnapshots =
        benchmarkSnapshots
          ?.filter((snapshot) => snapshot.isEnabled && !snapshot.deletedAt)
          ?.map((snapshot: BenchmarkSnapshot) => ({
            value: snapshot.id,
            label: snapshot.title,
          })) ?? []
      return [...options, ...enabledSnapshots]
    }
    return options
  }, [benchmarkSnapshots])

  // Initialize benchmark snapshot from localStorage
  useEffect(() => {
    if (!benchmarkSnapshots?.length || hasInitializedFromCache.current) return

    hasInitializedFromCache.current = true

    const cached = localStorage.getItem(STORAGE_KEY)
    if (!cached) {
      setSelectedBenchmarkSnapshot(null)
      return
    }

    // Convert cached string to number to match option value types
    const cachedId = Number(cached)
    const isSelectable = benchmarkSnapshots.some(
      (s: BenchmarkSnapshot) => s.id === cachedId && s.isEnabled && !s.deletedAt
    )
    if (!isSelectable) {
      localStorage.removeItem(STORAGE_KEY)
    }
    setSelectedBenchmarkSnapshot(isSelectable ? cachedId : null)
  }, [benchmarkSnapshots])

  const { mutate: onUpload, isPending: isUploading } = useMutation({
    mutationFn: async (options: any) => {
      try {
        const data = new FormData()
        data.append('file', options.file)
        data.append('uploadedBy', userName)
        data.append('year', selectedYear.toString())
        await client.post(`/customers/${id}/contributions/upload`, data)
        notification.success({
          message: 'Contribution uploaded successfully',
        })
        queryClient.fetchQuery({
          queryKey: ['getContributions', id, selectedYear],
          queryFn: () => getContributions(id, selectedYear),
        })
      } catch (error) {
        notification.error({
          message: 'Failed to upload contribution',
          description: (error as Error).message,
        })
      }
    },
  })

  const onChangeYear = (year: number) => {
    setSelectedYear(year)
  }

  const onChangeBenchmarkSnapshot = (benchmarkSnapshotId: number | null) => {
    setSelectedBenchmarkSnapshot(benchmarkSnapshotId)
    if (benchmarkSnapshotId !== null) {
      localStorage.setItem(STORAGE_KEY, String(benchmarkSnapshotId))
    } else {
      localStorage.removeItem(STORAGE_KEY)
    }
  }

  const onSelectContributionRule = (contributionRule: any) => {
    setSelectedContributionRule(contributionRule)
  }

  //

  return {
    isLoading,
    error,
    data,
    yearOptions,
    onUpload,
    onChangeYear,
    selectedYear,
    isUploading,
    selectedContributionRule,
    setSelectedContributionRule: onSelectContributionRule,
    benchmarkSnapshots,
    benchmarkSnapshotOptions,
    selectedBenchmarkSnapshot,
    onChangeBenchmarkSnapshot,
    isBenchmarkSnapshotsLoading,
    refetchContributions,
  }
}
